import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import NavbarAll from "../Navbar/NavbarAll";

function DashboardAdmin({ user, onLogout }) {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTeacher, setNewTeacher] = useState({ name: "", email: "", password: "" });
  const [newStudent, setNewStudent] = useState({ name: "", email: "", password: "", teacherId: "" });
  const [report, setReport] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", qariId: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const apiURL = process.env.REACT_APP_API_URL
  
  const token = localStorage.getItem("token");

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/hifz-login", { replace: true });
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const res = await axios.get(`${apiURL}/teachers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeachers(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const fetchReport = async () => {   // 🔹 NEW FUNCTION
    try {
      const res = await axios.get(`${apiURL}/report/teachers-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data);
    } catch (err) {
      console.error("Failed to fetch report:", err);
      alert("Failed to load report");
    }
  };

  const createTeacher = async () => {
    try {
      await axios.post(`${apiURL}/teachers`, newTeacher, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Teacher created successfully");
      setNewTeacher({ name: "", email: "", password: "" });
      fetchTeachers();
    } catch (err) {
      console.error("Teacher creation failed:", err);
      alert("Failed to create teacher");
    }
  };

  const createStudent = async () => {
    if (!newStudent.teacherId) {
      alert("Please assign the student to a teacher");
      return;
    }
    try {
      await axios.post(`${apiURL}/students`, newStudent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Student created successfully");
      setNewStudent({ name: "", email: "", password: "", teacherId: "" });
      fetchTeachers();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Failed to create student");
    }
  };

  const deleteUser = async (userId, name, role) => {
    const label = role === "qari" ? "teacher" : "student";
    if (!window.confirm(`Delete ${label} "${name}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${apiURL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(`${label.charAt(0).toUpperCase() + label.slice(1)} deleted successfully`);
      fetchTeachers();
      if (report.length > 0) fetchReport();
    } catch (err) {
      console.error("Delete failed:", err);
      const msg = err.response?.data?.error || `Failed to delete ${label}`;
      alert(msg);
    }
  };

  const openEditStudent = (student, currentTeacherId) => {
    setEditingStudent({ ...student, qariId: currentTeacherId });
    setEditForm({ name: student.name || "", qariId: currentTeacherId || "" });
  };

  const closeEditStudent = () => {
    setEditingStudent(null);
    setEditForm({ name: "", qariId: "" });
  };

  const updateStudent = async () => {
    if (!editingStudent) return;
    const trimmedName = editForm.name.trim();
    if (!trimmedName) {
      alert("Student name cannot be empty");
      return;
    }
    if (!editForm.qariId) {
      alert("Please assign the student to a teacher");
      return;
    }

    setSavingEdit(true);
    try {
      await axios.put(
        `${apiURL}/users/${editingStudent.id}`,
        { name: trimmedName, qariId: editForm.qariId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const transferred = editForm.qariId !== editingStudent.qariId;
      closeEditStudent();
      fetchTeachers();
      if (report.length > 0) fetchReport();
      alert(
        transferred
          ? "Student updated and transferred successfully"
          : "Student updated successfully"
      );
    } catch (err) {
      console.error("Update failed:", err);
      alert(err.response?.data?.error || "Failed to update student");
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    if (!editingStudent) return;
    const onEscape = (e) => {
      if (e.key === "Escape") closeEditStudent();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onEscape);
    };
  }, [editingStudent]);

  if (loading) return <p>Loading...</p>;

  return (
    <>
    <NavbarAll />
    <Background>
      <DashboardContainer>
        <AdminHeader>
          <Heading>📊 Admin Dashboard</Heading>
          <LogoutButton type="button" onClick={handleLogout} aria-label="Log out">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Logout
          </LogoutButton>
        </AdminHeader>
        <Button onClick={fetchReport} style={{ marginBottom: "20px" }}>
            📑 Generate Report (Teachers & Students)
          </Button>

        <FormWrapper>
          <FormBox>
            <h3>Create Teacher</h3>
            <Input
              type="text"
              placeholder="Name"
              value={newTeacher.name}
              onChange={(e) => setNewTeacher({ ...newTeacher, name: e.target.value })}
            />
            <Input
              type="email"
              placeholder="Email"
              value={newTeacher.email}
              onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
            />
            <Input
              type="password"
              placeholder="Password"
              value={newTeacher.password}
              onChange={(e) => setNewTeacher({ ...newTeacher, password: e.target.value })}
            />
            <Button onClick={createTeacher}>Add Teacher</Button>
          </FormBox>

          <FormBox>
            <h3>Create Student</h3>
            <Input
              type="text"
              placeholder="Name"
              value={newStudent.name}
              onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
            />
            <Input
              type="email"
              placeholder="Email"
              value={newStudent.email}
              onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
            />
            <Input
              type="password"
              placeholder="Password"
              value={newStudent.password}
              onChange={(e) => setNewStudent({ ...newStudent, password: e.target.value })}
            />
            <Select
              value={newStudent.teacherId}
              onChange={(e) => setNewStudent({ ...newStudent, teacherId: e.target.value })}
            >
              <option value="">Assign to Teacher</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            <Button onClick={createStudent}>Add Student</Button>
          </FormBox>
        </FormWrapper>

        <TeacherSection>
          <h3>👨‍🏫 Teachers & Students</h3>
          {teachers.map((teacher) => (
            <TeacherCard key={teacher.id}>
              <TeacherHeader>
                <h4>{teacher.name} ({teacher.email})</h4>
                <DeleteButton
                  type="button"
                  onClick={() => deleteUser(teacher.id, teacher.name, "qari")}
                >
                  Delete Teacher
                </DeleteButton>
              </TeacherHeader>
              <ul>
                {teacher.students?.length > 0 ? (
                  teacher.students.map((s) => (
                    <StudentRow key={s.id}>
                      <span>{s.name} ({s.email})</span>
                      <RowActions>
                        <UpdateButton
                          type="button"
                          onClick={() => openEditStudent(s, teacher.id)}
                        >
                          Update
                        </UpdateButton>
                        <DeleteButton
                          type="button"
                          onClick={() => deleteUser(s.id, s.name, "student")}
                        >
                          Delete
                        </DeleteButton>
                      </RowActions>
                    </StudentRow>
                  ))
                ) : (
                  <li>No students yet</li>
                )}
              </ul>
            </TeacherCard>
          ))}
        </TeacherSection>

        {report.length > 0 && (
            <TeacherSection>
              <h3>📑 Owner Report</h3>
              {report.map((teacher) => (
                <TeacherCard key={teacher.teacherId}>
                  <h4>{teacher.teacherName} ({teacher.teacherEmail})</h4>
                  <ul>
                    {teacher.students.length > 0 ? (
                      teacher.students.map((s) => (
                        <li key={s.id}>
                          {s.name} ({s.email}) — 
                          <a href={s.deepLink} target="_blank" rel="noreferrer"> Open Link </a>
                        </li>
                      ))
                    ) : (
                      <p>No students yet</p>
                    )}
                  </ul>
                </TeacherCard>
              ))}
            </TeacherSection>
          )}
      </DashboardContainer>
    </Background>

    {editingStudent &&
      createPortal(
        <ModalOverlay onClick={closeEditStudent} role="dialog" aria-modal="true" aria-labelledby="edit-student-title">
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalClose type="button" onClick={closeEditStudent} aria-label="Close">
              ×
            </ModalClose>
            <ModalTitle id="edit-student-title">Update Student</ModalTitle>
            <ModalHint>
              Edit the student's name or move them to another teacher's class.
              Transferring keeps all of their existing progress and attendance.
            </ModalHint>

            <FieldLabel htmlFor="edit-student-name">Name</FieldLabel>
            <Input
              id="edit-student-name"
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              disabled={savingEdit}
            />

            <FieldLabel>Email</FieldLabel>
            <ReadOnlyField>{editingStudent.email || "—"}</ReadOnlyField>

            <FieldLabel htmlFor="edit-student-teacher">Teacher (class)</FieldLabel>
            <Select
              id="edit-student-teacher"
              value={editForm.qariId}
              onChange={(e) => setEditForm({ ...editForm, qariId: e.target.value })}
              disabled={savingEdit}
            >
              <option value="">Assign to Teacher</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.email})
                </option>
              ))}
            </Select>
            {editForm.qariId && editForm.qariId !== editingStudent.qariId && (
              <TransferNote>This student will be transferred to a new class.</TransferNote>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={closeEditStudent} disabled={savingEdit}>
                Cancel
              </CancelButton>
              <Button type="button" onClick={updateStudent} disabled={savingEdit} style={{ flex: 1 }}>
                {savingEdit ? "Saving…" : "Save changes"}
              </Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>,
        document.body
      )}
    </>
  );
}

export default DashboardAdmin;

//
// ------------------- Styled Components -------------------
// White & green theme (Zakaria Masjid)
//
const Background = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #eef6ef 100%);
  padding: 40px 20px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: auto;
`;

const AdminHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;

  @media (max-width: 560px) {
    flex-direction: column;
    gap: 14px;
  }
`;

const Heading = styled.h2`
  text-align: center;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0;
  color: #005030;
`;

const LogoutButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #e2ede4;
  border-radius: 999px;
  background: #ffffff;
  color: #5f7167;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.2s, border-color 0.2s, background 0.2s;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: #dc2626;
    border-color: #f5cccc;
    background: #fef4f4;
  }

  &:focus-visible {
    outline: 2px solid #47a447;
    outline-offset: 2px;
  }

  @media (max-width: 560px) {
    position: static;
    transform: none;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const FormBox = styled.div`
  flex: 1;
  min-width: 300px;
  background: #ffffff;
  border: 1px solid #e2ede4;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 6px 20px rgba(0, 80, 48, 0.07);

  h3 {
    margin-bottom: 15px;
    font-size: 1.3rem;
    font-weight: 700;
    color: #005030;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #d8e8db;
  border-radius: 8px;
  outline: none;
  font-size: 0.95rem;
  color: #1c2b22;
  transition: 0.2s;

  &:focus {
    border-color: #47a447;
    box-shadow: 0 0 0 3px rgba(71, 164, 71, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #d8e8db;
  border-radius: 8px;
  outline: none;
  font-size: 0.95rem;
  color: #1c2b22;
  background: #fff;

  &:focus {
    border-color: #47a447;
    box-shadow: 0 0 0 3px rgba(71, 164, 71, 0.2);
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #47a447, #398439);
  color: #fff;
  border: none;
  padding: 12px;
  width: 100%;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(71, 164, 71, 0.3);
  transition: 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.04);
    box-shadow: 0 6px 16px rgba(71, 164, 71, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const TeacherSection = styled.div`
  margin-top: 40px;

  h3 {
    margin-bottom: 20px;
    font-size: 1.5rem;
    font-weight: 800;
    color: #005030;
  }
`;

const TeacherCard = styled.div`
  background: #fff;
  border: 1px solid #e2ede4;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 15px;
  box-shadow: 0 4px 14px rgba(0, 80, 48, 0.06);

  h4 {
    margin-bottom: 10px;
    color: #1c2b22;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    color: #5f7167;
    margin-bottom: 6px;
  }

  a {
    color: #398439;
    font-weight: 600;
  }
`;

const TeacherHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;

  h4 {
    margin-bottom: 0;
  }
`;

const StudentRow = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DeleteButton = styled.button`
  background: #e53935;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  width: auto;
  transition: 0.2s;

  &:hover {
    background: #c62828;
    transform: none;
    box-shadow: 0 4px 8px rgba(229, 57, 53, 0.3);
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const UpdateButton = styled.button`
  background: #ffffff;
  color: #005030;
  border: 1px solid #c9e2cd;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  width: auto;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: #eaf5eb;
    border-color: #47a447;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 32, 16, 0.5);
  backdrop-filter: blur(6px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20000;
  padding: 16px;
  box-sizing: border-box;
`;

const ModalCard = styled.div`
  position: relative;
  background: #fff;
  border-radius: 16px;
  border-top: 4px solid #47a447;
  padding: 28px 24px 24px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 24px 60px rgba(0, 32, 16, 0.28);
  animation: adminModalFadeIn 0.22s ease-out;

  @keyframes adminModalFadeIn {
    from { opacity: 0; transform: scale(0.96) translateY(10px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #eef4ef;
  color: #5f7167;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #e2ede4;
    color: #005030;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 32px 8px 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #005030;
`;

const ModalHint = styled.p`
  margin: 0 0 18px;
  font-size: 0.85rem;
  color: #5f7167;
  line-height: 1.5;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #33473c;
`;

const ReadOnlyField = styled.div`
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #e2ede4;
  border-radius: 8px;
  background: #f4faf5;
  color: #5f7167;
  font-size: 0.95rem;
  word-break: break-all;
`;

const TransferNote = styled.p`
  margin: -4px 0 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #398439;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  border: 1px solid #d8e8db;
  border-radius: 10px;
  background: #f4faf5;
  color: #33473c;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #eaf5eb;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
